import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, Camera, Loader2 } from "lucide-react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";

// A calm, editorial gallery — Kingdom Blue canvas with gold accents.
// Category chips filter the grid; clicking any image opens a full-viewport lightbox
// with keyboard + click navigation between images.
export default function Gallery() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(["Ministry", "Events", "Community", "Media"]);
  const [active, setActive] = useState("All");
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  useEffect(() => {
    Promise.all([
      api.get("/gallery"),
      api.get("/gallery/categories"),
    ])
      .then(([g, c]) => {
        setItems(g.data || []);
        setCategories(c.data?.categories || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (active === "All") return items;
    return items.filter((i) => i.category === active);
  }, [items, active]);

  // Keyboard nav in lightbox
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(-1);
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % filtered.length);
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while lightbox is open
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, filtered.length]);

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(-1);
  const next = (e) => { e?.stopPropagation(); setLightboxIdx((i) => (i + 1) % filtered.length); };
  const prev = (e) => { e?.stopPropagation(); setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length); };

  return (
    <div data-testid="gallery-page" className="bg-ink">
      <PageHero
        overline="Assorted Photos"
        title={<>The <span className="font-accent italic text-gold-gradient">Gallery</span></>}
        subtitle="Moments from the ministry, gatherings, community life, and media — captured across seasons."
      />

      {/* Filter chips */}
      <section className="border-b border-gold-500/15">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-8">
          <div className="flex flex-wrap items-center gap-2" data-testid="gallery-filters">
            {["All", ...categories].map((cat) => {
              const isActive = active === cat;
              const count = cat === "All" ? items.length : items.filter((i) => i.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => setActive(cat)}
                  data-testid={`gallery-filter-${cat.toLowerCase()}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.2em] border transition-colors duration-300 ${
                    isActive
                      ? "border-gold-400 bg-gold-500/12 text-gold-200"
                      : "border-white/12 bg-ink-paper/40 text-white/70 hover:border-gold-500/50 hover:text-gold-300"
                  }`}
                >
                  {cat}
                  <span className={`text-[10px] ${isActive ? "text-gold-300/80" : "text-white/40"}`}>({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-20">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-white/50" data-testid="gallery-loading">
            <Loader2 size={22} className="animate-spin mr-3 text-gold-400" />
            <span className="text-sm uppercase tracking-[0.2em]">Loading gallery…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="gallery-empty">
            <Camera size={40} strokeWidth={1.3} className="text-gold-500/60 mb-5" />
            <p className="font-serif text-2xl text-white">No photos in this category yet.</p>
            <p className="mt-3 text-sm text-white/50 max-w-md">Check back soon — new photos are added after every gathering.</p>
          </div>
        ) : (
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-5 [column-fill:_balance]"
            data-testid="gallery-grid"
          >
            {filtered.map((item, idx) => (
              <motion.button
                key={item.id}
                onClick={() => openLightbox(idx)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`gallery-item-${item.id}`}
                className="group relative mb-4 md:mb-5 block w-full break-inside-avoid overflow-hidden border border-gold-500/20 bg-ink-paper text-left"
              >
                <img
                  src={item.image_url}
                  alt={item.title}
                  loading="lazy"
                  className="w-full h-auto object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                />
                {/* Bottom overlay caption */}
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 bg-gradient-to-t from-ink via-ink/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <p className="text-[10px] uppercase tracking-[0.28em] text-gold-400">{item.category}</p>
                  <p className="mt-1.5 font-serif text-white text-lg md:text-xl leading-tight">{item.title}</p>
                </div>
                {/* Zoom badge */}
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-ink/70 backdrop-blur border border-gold-500/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <ZoomIn size={14} className="text-gold-400" />
                </div>
              </motion.button>
            ))}
          </motion.div>
        )}
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx >= 0 && filtered[lightboxIdx] && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex flex-col items-center justify-center p-4 md:p-8"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              aria-label="Close"
              data-testid="gallery-lightbox-close"
              className="absolute top-5 right-5 md:top-8 md:right-8 w-11 h-11 rounded-full bg-ink border border-gold-500/40 text-white/80 hover:text-gold-300 hover:border-gold-400 flex items-center justify-center transition-colors z-10"
            >
              <X size={20} />
            </button>
            {/* Prev / Next */}
            {filtered.length > 1 && (
              <>
                <button
                  onClick={prev}
                  aria-label="Previous"
                  data-testid="gallery-lightbox-prev"
                  className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-ink/80 border border-gold-500/30 text-white/80 hover:text-gold-300 hover:border-gold-400 flex items-center justify-center transition-colors z-10"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  onClick={next}
                  aria-label="Next"
                  data-testid="gallery-lightbox-next"
                  className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-ink/80 border border-gold-500/30 text-white/80 hover:text-gold-300 hover:border-gold-400 flex items-center justify-center transition-colors z-10"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            )}
            {/* Image */}
            <motion.div
              key={filtered[lightboxIdx].id}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-[95vw] max-h-[85vh] flex flex-col items-center"
            >
              <img
                src={filtered[lightboxIdx].image_url}
                alt={filtered[lightboxIdx].title}
                className="max-w-[95vw] max-h-[75vh] w-auto h-auto object-contain border border-gold-500/25"
                data-testid="gallery-lightbox-image"
              />
              {/* Caption */}
              <div className="mt-5 text-center max-w-2xl">
                <p className="text-[10px] uppercase tracking-[0.28em] text-gold-400">{filtered[lightboxIdx].category}</p>
                <p className="mt-2 font-serif text-white text-2xl md:text-3xl leading-tight">
                  {filtered[lightboxIdx].title}
                </p>
                {filtered[lightboxIdx].description && (
                  <p className="mt-2 text-white/60 text-sm leading-relaxed">{filtered[lightboxIdx].description}</p>
                )}
                <p className="mt-4 text-[10px] uppercase tracking-[0.24em] text-white/40">
                  {lightboxIdx + 1} / {filtered.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
